import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import FilterButton from '@/components/FilterDropdown/FilterButton';
import { EditorialQueuePage } from '@/types/resources';
import downloadCsv from '@/utils/csv';

import BlacklistModal from './Blacklist/BlacklistModal';
import ExportModal from './ExportModal';
import GSheetImportModal from './GSheetImportModal';
import ImportModal from './ImportModal';
import MoreActionsDrawer from './MoreActionsDrawer';
import NotificationsSettingsModal from './NotificationsSettingsModal';
import OverviewModal from './Overview/Modal';
import PagesAndGroupsInfoModal from './PagesAndGroupsInfoModal';
import PerformanceModal from './PerformanceModal';
import RepostAutomationModal from './RepostAutomationModal';
import ResorterSettingsModal from './ResorterSettingsModal';

const StyledFilterButton = styled(FilterButton)`
  margin: 0 !important;
  border-color: ${({ theme }) => theme.colors.grayscale};
  padding-left: 16px;
  padding-right: 16px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.lightGray};
  }
`;

export type ActionType =
  | 'upload-csv'
  | 'download-csv'
  | 'export-csv'
  | 'gsheet-import'
  | 'performance'
  | 'overview'
  | 'pages-modal'
  | 'resorter-settings'
  | 'blacklist'
  | 'notifications-settings'
  | 'repost-automation';

interface Props {
  activePage: EditorialQueuePage;
}

const MoreActions: FC<Props> = ({ activePage }) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showGSheetImportModal, setShowGSheetImportModal] = useState(false);
  const [showOverviewModal, setShowOverviewModal] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [showResorterSettings, setShowRestorterSettings] = useState(false);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [showNotificationsSettingsModal, setShowNotificationsSettingsModal] =
    useState(false);
  const [showRepostAutomationModal, setShowRepostAutomationModal] =
    useState(false);

  const handleAction = (actionType: ActionType) => {
    setShowDrawer(false);

    switch (actionType) {
      case 'upload-csv':
        setShowImportModal(true);
        break;
      case 'download-csv':
        downloadCsv([], 'template', [
          'type',
          'article_link',
          'is_fresh',
          'image',
          'video',
          'message',
          'description',
          'comment',
          'comment_delay',
          'site_visit_benchmark',
          'page_id',
          'group_id',
          'pinterest_board_id',
          'additional_images',
          'use_shorter_url',
          'auto_comment',
          'auto_comment_delay',
          'images',
          'notes',
          'fresh_id',
          'production_team',
          'compilation_type',
          'project_id',
        ]);
        break;
      case 'export-csv':
        setShowExportModal(true);
        break;
      case 'gsheet-import':
        setShowGSheetImportModal(true);
        break;
      case 'performance':
        setShowPerformanceModal(true);
        break;
      case 'overview':
        setShowOverviewModal(true);
        break;
      case 'pages-modal':
        setShowInfoModal(true);
        break;
      case 'resorter-settings':
        setShowRestorterSettings(true);
        break;
      case 'blacklist':
        setShowBlacklistModal(true);
        break;
      case 'notifications-settings':
        setShowNotificationsSettingsModal(true);
        break;
      case 'repost-automation':
        setShowRepostAutomationModal(true);
        break;
    }
  };

  return (
    <>
      <StyledFilterButton
        text="More"
        filterApplied={false}
        isActive={false}
        onClick={() => setShowDrawer(true)}
        icon={
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.5 9.5C4.32843 9.5 5 8.82843 5 8C5 7.17157 4.32843 6.5 3.5 6.5C2.67157 6.5 2 7.17157 2 8C2 8.82843 2.67157 9.5 3.5 9.5ZM8 9.5C8.82843 9.5 9.5 8.82843 9.5 8C9.5 7.17157 8.82843 6.5 8 6.5C7.17157 6.5 6.5 7.17157 6.5 8C6.5 8.82843 7.17157 9.5 8 9.5ZM14 8C14 8.82843 13.3284 9.5 12.5 9.5C11.6716 9.5 11 8.82843 11 8C11 7.17157 11.6716 6.5 12.5 6.5C13.3284 6.5 14 7.17157 14 8Z"
              fill="currentColor"
            />
          </svg>
        }
      />
      <AnimatePresence>
        {showDrawer && (
          <MoreActionsDrawer
            onClose={() => setShowDrawer(false)}
            onActionClick={handleAction}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showImportModal && (
          <ImportModal
            onClose={() => setShowImportModal(false)}
            activePage={activePage}
          />
        )}
        {showExportModal && (
          <ExportModal onClose={() => setShowExportModal(false)} />
        )}
        {showGSheetImportModal && (
          <GSheetImportModal onClose={() => setShowGSheetImportModal(false)} />
        )}
        {showInfoModal && (
          <PagesAndGroupsInfoModal onClose={() => setShowInfoModal(false)} />
        )}
        {showOverviewModal && (
          <OverviewModal onClose={() => setShowOverviewModal(false)} />
        )}
        {showPerformanceModal && (
          <PerformanceModal
            onClose={() => setShowPerformanceModal(false)}
            activePage={activePage}
          />
        )}
        {showResorterSettings && (
          <ResorterSettingsModal
            onClose={() => setShowRestorterSettings(false)}
            activePage={activePage}
          />
        )}
        {showBlacklistModal && (
          <BlacklistModal onClose={() => setShowBlacklistModal(false)} />
        )}
        {showNotificationsSettingsModal && (
          <NotificationsSettingsModal
            onClose={() => setShowNotificationsSettingsModal(false)}
            activePage={activePage}
          />
        )}
        {showRepostAutomationModal && (
          <RepostAutomationModal
            onClose={() => setShowRepostAutomationModal(false)}
            activePage={activePage}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default MoreActions;
